import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { useAuth } from '@/lib/auth/useAuth';

interface VerifySearch {
  email?: string;
  next?: string;
}

export const Route = createFileRoute('/auth/verify')({
  validateSearch: (search): VerifySearch => ({
    email: typeof search.email === 'string' ? search.email : undefined,
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
  component: VerifyRoute,
});

function VerifyRoute() {
  const { email, next } = Route.useSearch();
  const { signInWithMagicLink, signOut } = useAuth();
  const [cooldown, setCooldown] = useState(0);
  const [status, setStatus] = useState<string | null>(null);

  const resend = async () => {
    if (!email || cooldown > 0) return;
    setStatus(null);
    try {
      // Forward next so a re-sent link lands where the original intended.
      await signInWithMagicLink(email, next);
      setStatus('Verification email re-sent.');
      setCooldown(30);
      const id = window.setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) {
            window.clearInterval(id);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Unable to resend');
    }
  };

  return (
    <AuthLayout
      title="Check your email"
      subtitle={
        email
          ? `We sent a verification link to ${email}. Open it to finish creating your account.`
          : 'We sent a verification link to your email address. Open it to finish creating your account.'
      }
    >
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => void resend()}
          disabled={!email || cooldown > 0}
          className="w-full rounded-md border border-ink/20 px-4 py-2.5 font-sans text-sm font-medium text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-60"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend verification email'}
        </button>
        {status && (
          <p role="status" aria-live="polite" className="text-sm text-ink/70">
            {status}
          </p>
        )}
        <button
          type="button"
          onClick={() => void signOut()}
          className="block w-full text-center text-xs uppercase tracking-[0.12em] text-ink/50 hover:text-oxblood"
        >
          Sign out
        </button>
      </div>
    </AuthLayout>
  );
}
